import * as React from 'react';
import { createFileRoute, Outlet, Link, useRouterState } from '@tanstack/react-router';
import { Check, Circle, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui/skeleton';
import { useBrief } from '@/lib/brief';

export const Route = createFileRoute('/_app/brief')({
  staticData: { crumb: 'Treść strony' },
  component: BriefLayout,
});

type SectionLink = {
  to: string;
  label: string;
  hint: string;
  exact?: boolean;
  /** Returns true when this section has enough data to render the live site
   *  cleanly. Mirrors the section-level gates in the generator pipeline. */
  isComplete: (brief: Record<string, unknown>) => boolean;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const SECTIONS: readonly SectionLink[] = [
  {
    to: '/brief',
    label: 'Dane podstawowe',
    hint: 'NIP, adres, godziny, kontakt',
    exact: true,
    isComplete: (b: any) =>
      !!(b?.legal?.nip && b?.address?.city && (b?.hours?.length ?? 0) > 0),
  },
  {
    to: '/brief/team',
    label: 'Zespół',
    hint: 'min. 2 osoby ze zdjęciami',
    isComplete: (b: any) =>
      ((b?.team ?? []).filter((m: any) => m.photo_url).length ?? 0) >= 2,
  },
  {
    to: '/brief/services',
    label: 'Usługi',
    hint: 'min. 2 oferowane zabiegi',
    isComplete: (b: any) =>
      ((b?.inline_services ?? []).length ?? 0) >= 2 ||
      ((b?.procedures ?? []).filter((p: any) => p.offered !== false).length ?? 0) >= 2,
  },
  {
    to: '/brief/menu',
    label: 'Menu nawigacji',
    hint: 'opcjonalne — autogenerowane',
    isComplete: (b: any) =>
      (b?.nav_override?.columns?.length ?? 0) > 0 ||
      ((b?.procedures ?? []).filter((p: any) => p.offered !== false).length ?? 0) >= 4,
  },
  {
    to: '/brief/booking',
    label: 'Rezerwacje',
    hint: 'provider + URL',
    isComplete: (b: any) => !!b?.booking?.provider && b.booking.provider !== 'none' ? !!b.booking.url : true,
  },
  {
    to: '/brief/faq',
    label: 'FAQ',
    hint: 'min. 3 pytania',
    isComplete: (b: any) => (b?.faq?.length ?? 0) >= 3,
  },
  {
    to: '/brief/testimonials',
    label: 'Opinie',
    hint: 'opcjonalne',
    isComplete: (b: any) => (b?.testimonials?.length ?? 0) >= 1,
  },
  {
    to: '/brief/identity',
    label: 'O klinice',
    hint: 'misja + założenie',
    isComplete: (b: any) => !!(b?.identity?.mission || b?.identity?.manifesto),
  },
  {
    to: '/brief/equipment',
    label: 'Sprzęt',
    hint: 'opcjonalne',
    isComplete: (b: any) => (b?.equipment?.length ?? 0) >= 1,
  },
  {
    to: '/brief/accreditations',
    label: 'Akredytacje',
    hint: 'opcjonalne',
    isComplete: (b: any) =>
      (b?.accreditations?.length ?? 0) >= 1 || (b?.memberships?.length ?? 0) >= 1,
  },
];
/* eslint-enable @typescript-eslint/no-explicit-any */

function BriefLayout() {
  const { data: brief, isPending } = useBrief();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const sections = SECTIONS.map((s) => ({
    ...s,
    done: brief ? s.isComplete(brief as unknown as Record<string, unknown>) : false,
  }));
  const doneCount = sections.filter((s) => s.done).length;
  const total = sections.length;

  return (
    <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[16rem_minmax(0,1fr)]">
      <aside className="lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
        <div className="px-2 pb-3">
          <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">
            Sekcje treści
          </p>
          <div className="mt-2 flex items-center gap-2 text-xs text-text-muted">
            <span className="tabular-nums">
              {doneCount} / {total} ukończone
            </span>
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-bg ring-1 ring-border">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${(doneCount / total) * 100}%` }}
              />
            </div>
          </div>
        </div>
        <nav aria-label="Sekcje treści" className="flex flex-col gap-0.5">
          {sections.map((s) => {
            const active = s.exact ? pathname === s.to : pathname.startsWith(s.to);
            return (
              <Link
                key={s.to}
                to={s.to as never}
                className={cn(
                  'group flex items-start gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                  active
                    ? 'bg-bg text-text font-medium shadow-[0_1px_2px_rgba(0,0,0,0.04)] ring-1 ring-black/5'
                    : 'text-text-muted hover:bg-bg/60 hover:text-text',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'mt-0.5 inline-grid size-4 shrink-0 place-items-center rounded-full',
                    s.done
                      ? 'bg-success/20 text-success'
                      : active
                        ? 'bg-accent/15 text-accent'
                        : 'bg-bg ring-1 ring-border text-text-subtle',
                  )}
                >
                  {s.done ? (
                    <Check className="size-2.5" />
                  ) : (
                    <Circle className="size-2 fill-current" />
                  )}
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate">{s.label}</span>
                  <span
                    className={cn(
                      'text-[11px] text-text-subtle',
                      active ? 'text-text-muted' : '',
                    )}
                  >
                    {s.hint}
                  </span>
                </span>
                <ChevronRight
                  className="ml-auto mt-1 size-3 text-text-subtle opacity-0 transition-opacity group-hover:opacity-100"
                  aria-hidden="true"
                />
              </Link>
            );
          })}
        </nav>
      </aside>

      <div className="min-w-0">
        {isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-64" />
            <Skeleton className="h-5 w-96" />
            <Skeleton className="mt-6 h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
}
